import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as authAPI from "../../api/auth";
import { normalizeTokenPayload } from "../../api/tokenUtils";
import { resolveCapabilities } from "../../access/capabilities";
import { normalizeRole } from "../../access/roles";
import { toast } from "react-toastify";
import { addAsyncCases } from "../utils/asyncReducers";
import {
  createAuthMutationThunk,
  getAuthErrorMessage,
  getAuthResponseMessage,
} from "./utils";

const initialState = {
  isAuth: false,
  data: null,
  token: null,
  refreshToken: null,
  status: null,
  refreshTokenExpiresIn: null,
  expires: null,
  error: null,
  // admin: false,
  userloginid: null,
  id: null,
  user: null,
  email: null,
  role: "undefined",
  clientId:null,
  businessId:null,
  allowedBusinessIds: null,
  capabilities: null,
  authBypassed: false,
  accessContextReady: false,
  ui: {
    msg: "",
    loading: false,
  },
};
const getAuthState = (state) => state.auth;

const clearResolvedAccessState = (state, { markReady = false } = {}) => {
  state.role = "undefined";
  state.clientId = null;
  state.businessId = null;
  state.allowedBusinessIds = null;
  state.capabilities = null;
  state.authBypassed = false;
  state.accessContextReady = markReady;
};

const clearSessionState = (state, { markReady = false } = {}) => {
  state.isAuth = false;
  state.data = null;
  state.token = null;
  state.refreshToken = null;
  state.refreshTokenExpiresIn = null;
  state.expires = null;
  state.error = null;
  state.userloginid = null;
  state.id = null;
  state.user = null;
  state.email = null;
  state.status = markReady ? "sessionExpired" : null;
  state.ui = {
    msg: "",
    loading: false,
  };
  clearResolvedAccessState(state, { markReady });
};

const normalizeAccessContext = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const rawRole = payload.role || payload.userType || payload.roleName;

  return {
    email: payload.email || null,
    role: rawRole ? normalizeRole(rawRole) : null,
    clientId: payload.clientId ?? null,
    businessId: payload.businessId ?? null,
    allowedBusinessIds: Array.isArray(payload.allowedBusinessIds)
      ? payload.allowedBusinessIds.map((id) => String(id))
      : payload.allowedBusinessIds === null
        ? null
        : [],
    capabilities: payload.capabilities || null,
    authBypassed: Boolean(payload.authBypassed),
  };
};

const applyAccessContextToState = (state, payload) => {
  const accessContext = normalizeAccessContext(payload);

  if (!accessContext) {
    return false;
  }

  state.authBypassed = accessContext.authBypassed;
  state.email = accessContext.email;
  state.user = accessContext.email;
  state.role = accessContext.role || state.role;
  state.clientId = accessContext.clientId;
  state.businessId = accessContext.businessId;
  state.allowedBusinessIds = accessContext.allowedBusinessIds;
  state.capabilities = accessContext.capabilities;
  state.accessContextReady = true;
  return true;
};

const isUnauthorizedAccessError = (error) =>
  Number(error?.statusCode) === 401 || Number(error?.response?.status) === 401;

const login = createAsyncThunk(
  "auth/login",
  async (params, { rejectWithValue }) => {
    try {
      const res = await authAPI.login(params);
      const data = res.data;

      // Legacy shape: { statusCodeValue, body }
      if (typeof data?.statusCodeValue === "number") {
        if (data.statusCodeValue === 200) {
          toast.success("Login successful!");
        } else {
          toast.error("Login failed. Please check your credentials.");
        }
        if (data.statusCodeValue >= 400) {
          return rejectWithValue(
            data.body?.error || data.body || "Login failed"
          );
        }
        const authPayload = normalizeTokenPayload({
          ...data.body,
          email: params.email,
        });
        const accessContext = await authAPI
          .resolveAccessContext(authPayload.accessToken)
          .catch(() => null);
        return { ...authPayload, accessContext };
      }

      // New direct shape: TokenResponse { accessToken, expiresIn, ... }
      if (data && data.accessToken) {
        toast.success("Login successful!");
        const authPayload = normalizeTokenPayload({
          ...data,
          email: params.email,
        });
        const accessContext = await authAPI
          .resolveAccessContext(authPayload.accessToken)
          .catch(() => null);
        return { ...authPayload, accessContext };
      }

      toast.error("Login failed. Please check your credentials.");
      return rejectWithValue("Login failed");
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const signup = createAuthMutationThunk("signup", authAPI.signup, {
  successMessage: "Signup completed successfully",
  errorMessage: "Signup failed",
});

const reset = createAuthMutationThunk("reset", authAPI.reset, {
  successMessage: "Password reset request accepted",
  errorMessage: "Unable to reset password",
  mapPayload: (payload) => ({
    message: getAuthResponseMessage(payload, "Password reset request accepted"),
  }),
});

const verify = createAuthMutationThunk("verify", authAPI.verify, {
  successMessage: "Email verified successfully",
  errorMessage: "Email verification failed",
});

const update = createAuthMutationThunk("update", authAPI.update, {
  successMessage: "Password updated successfully",
  errorMessage: "Unable to update password",
  mapPayload: (payload, params) => ({
    message: getAuthResponseMessage(payload, "Password updated successfully"),
    redirectTo: params.redirectTo || "/",
    logoutAfterSuccess: params.logoutAfterSuccess !== false,
  }),
});

const hydrateAccessContext = createAsyncThunk(
  "auth/hydrateAccessContext",
  async (params, { getState, rejectWithValue }) => {
    const authState = getState()?.auth || {};
    const accessToken = params?.token || authState.token;
    if (!accessToken) {
      return rejectWithValue({
        message: "Missing access token",
        clearSession: true,
      });
    }

    try {
      const accessContext = await authAPI.resolveAccessContext(accessToken);
      return { accessContext };
    } catch (error) {
      if (!isUnauthorizedAccessError(error)) {
        return rejectWithValue({
          message: getAuthErrorMessage(
            error,
            "Unable to resolve access context"
          ),
          clearSession: false,
        });
      }

      if (!authState.refreshToken) {
        return rejectWithValue({
          message: getAuthErrorMessage(
            error,
            "Unable to resolve access context"
          ),
          clearSession: true,
        });
      }

      try {
        const refreshResponse = await authAPI.refresh_token(
          authState.refreshToken
        );
        const refreshData = refreshResponse?.data;

        if (typeof refreshData?.statusCodeValue === "number") {
          if (refreshData.statusCodeValue >= 400) {
            return rejectWithValue(
              {
                message:
                  refreshData.body?.error ||
                  refreshData.body ||
                  "Unable to refresh session",
                clearSession: true,
              }
            );
          }
        }

        const refreshedAuthPayload = normalizeTokenPayload(
          refreshData?.body || refreshData
        );

        if (!refreshedAuthPayload.accessToken) {
          return rejectWithValue({
            message: "Missing refreshed access token",
            clearSession: true,
          });
        }

        const refreshedAccessContext = await authAPI.resolveAccessContext(
          refreshedAuthPayload.accessToken
        );

        return {
          accessContext: refreshedAccessContext,
          refreshedAuthPayload,
        };
      } catch (refreshError) {
        return rejectWithValue({
          message: getAuthErrorMessage(refreshError, "Unable to refresh session"),
          clearSession: true,
        });
      }
    }
  }
);

const bootstrapAccessContext = createAsyncThunk(
  "auth/bootstrapAccessContext",
  async (_, { rejectWithValue }) => {
    try {
      const accessContext = await authAPI.resolveAccessContext();

      if (!accessContext || typeof accessContext !== "object") {
        return rejectWithValue({
          silent: true,
          markReady: true,
        });
      }

      return { accessContext };
    } catch (error) {
      if (isUnauthorizedAccessError(error)) {
        return rejectWithValue({
          silent: true,
          markReady: true,
        });
      }

      return rejectWithValue({
        message: getAuthErrorMessage(error, "Unable to resolve access context"),
        markReady: true,
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,

  reducers: {
    persistLogin: (state) => {
      state.status = "idle";
    },
    setRefreshedTokenData: (state, action) => {
      state.ui.loading = false;
      const payload = normalizeTokenPayload(action.payload);

      // Guard against unexpected payloads
      if (!payload.accessToken) {
        clearSessionState(state);
        state.error = "Invalid refresh token payload";
        return;
      }

      state.token = payload.accessToken;
      state.expires = payload.expiresIn;
      state.isAuth = true;
      state.refreshToken = payload.refreshToken;
      state.refreshTokenExpiresIn = payload.refreshTokenExpiresIn;
      state.email = payload.email || state.email;
      state.user = payload.email || state.user;
      clearResolvedAccessState(state);
      applyAccessContextToState(state, payload.accessContext);
    },
    userregister: () => ({
      ...initialState,
      status: "unregistered",
    }),
    logout: (state) => {
      return { ...initialState, status: "loggingOut" };
    },
    sessionExpired: () => ({
      ...initialState,
      status: "sessionExpired",
      error: "sessionExpired",
    }),
    skipLogin: (state) => {
      clearSessionState(state, { markReady: true });
      state.role = "Admin";
      state.isAuth = false;
      state.capabilities = resolveCapabilities({
        role: "Admin",
        authBypassed: true,
      });
      state.authBypassed = true;
      state.accessContextReady = true;
      state.status = "fulfilled";
    },
    resetAuth: () => ({ ...initialState }),
    nullVerifyData: (state) => {
      state.data = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.ui.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.ui.loading = false;
        const payload = normalizeTokenPayload(action.payload);

        if (!payload.accessToken) {
          // Treat as failed login even if thunk resolved unexpectedly
          clearSessionState(state);
          state.error = "Invalid login payload";
          state.status = "rejected";
          return;
        }

        state.token = payload.accessToken;
        state.expires = payload.expiresIn;
        state.isAuth = true;
        state.refreshToken = payload.refreshToken;
        state.refreshTokenExpiresIn = payload.refreshTokenExpiresIn;
        state.userloginid = payload.idUserLogin;
        state.id = payload.id;
        state.email = payload.email || null;
        state.user = payload.email || null;
        state.status = "fulfilled";
        clearResolvedAccessState(state);
        applyAccessContextToState(state, payload.accessContext);
      })
      .addCase(login.rejected, (state, action) => {
        state.isAuth = false;
        state.ui.loading = false;
        state.ui.msg = action.payload;
      })
      .addCase(hydrateAccessContext.pending, (state) => {
        state.accessContextReady = false;
        state.error = null;
      })
      .addCase(hydrateAccessContext.fulfilled, (state, action) => {
        const refreshedAuthPayload = action.payload?.refreshedAuthPayload;

        state.isAuth = true;

        if (refreshedAuthPayload) {
          state.token = refreshedAuthPayload.accessToken;
          state.refreshToken = refreshedAuthPayload.refreshToken;
          state.expires = refreshedAuthPayload.expiresIn;
          state.refreshTokenExpiresIn =
            refreshedAuthPayload.refreshTokenExpiresIn;
          state.email = refreshedAuthPayload.email || state.email;
          state.user = state.email || state.user;
        }

        clearResolvedAccessState(state);
        applyAccessContextToState(
          state,
          action.payload?.accessContext || action.payload
        );
        state.error = null;
      })
      .addCase(hydrateAccessContext.rejected, (state, action) => {
        const message =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "Unable to resolve access context";

        if (action.payload?.clearSession) {
          clearSessionState(state, { markReady: true });
          state.error = message;
          return;
        }

        clearResolvedAccessState(state, { markReady: true });
        state.error = message;
      })
      .addCase(bootstrapAccessContext.pending, (state) => {
        state.accessContextReady = false;
        state.error = null;
      })
      .addCase(bootstrapAccessContext.fulfilled, (state, action) => {
        state.isAuth = false;
        clearResolvedAccessState(state);
        applyAccessContextToState(
          state,
          action.payload?.accessContext || action.payload
        );
        state.error = null;
      })
      .addCase(bootstrapAccessContext.rejected, (state, action) => {
        clearResolvedAccessState(state, {
          markReady: Boolean(action.payload?.markReady),
        });

        if (action.payload?.silent) {
          state.error = null;
          return;
        }

        state.error =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "Unable to resolve access context";
      });

    addAsyncCases(builder, reset, {
      dataKey: null,
    });

    addAsyncCases(builder, signup, {
      dataKey: null,
    });

    addAsyncCases(builder, verify, {
      dataKey: null,
      onFulfilled: (state, action) => {
        if (action.payload && typeof action.payload === "object") {
          state.id = action.payload.id;
        }
      },
    });

    addAsyncCases(builder, update, {
      dataKey: null,
      onFulfilled: (state, action) => {
        if (action.payload?.logoutAfterSuccess === false) {
          state.status = "fulfilled";
          return;
        }

        Object.assign(state, { ...initialState, status: "loggingOut" });
      },
    });
  },
});

const {
  persistLogin,
  logout,
  sessionExpired,
  skipLogin,
  resetAuth,
  setRefreshedTokenData,
  nullVerifyData,
} = authSlice.actions;

export default authSlice.reducer;

export {
  persistLogin,
  login,
  logout,
  sessionExpired,
  getAuthState,
  skipLogin,
  resetAuth,
  reset,
  signup,
  verify,
  update,
  hydrateAccessContext,
  bootstrapAccessContext,
  setRefreshedTokenData,
  nullVerifyData,
};
